import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Card, 
  Space, 
  Modal, 
  Form, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Alert,
  Spin,
  Tooltip,
  Popconfirm,
  message,
  Checkbox
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  CloseOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useClinics, useCreateClinic, useUpdateClinic, useDeleteClinic, useGraphQLError } from '../hooks/useGraphQL';
import { useTranslation } from '../hooks/useTranslation';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

interface Clinic {
  id: string;
  name_en: string;
  name_zh_hk: string;
  name_zh_cn?: string;
  internal_name?: string;
  address: string;
  phone_number: string;
  email: string;
  status: string;
  region: string;
  district: string;
  external_id?: string;
  district_id?: number;
  district_name?: string;
  iframe?: string;
  description?: string;
  building_name?: string;
  floor?: string;
  unit?: string;
  postal_code?: string;
  registration_number?: string;
  website_url?: string;
  emergency_services?: boolean;
  walk_in_enabled?: boolean;
  online_consultation_enabled?: boolean;
  appointment_booking_enabled?: boolean;
  facilities?: string[];
  services?: string[];
  images?: string[];
  latitude?: number;
  longitude?: number;
  created_at: string;
  updated_at: string;
}

const Clinics: React.FC = () => {
  const { t } = useTranslation();
  const { data, loading, error } = useClinics();
  const [createClinic] = useCreateClinic();
  const [updateClinic] = useUpdateClinic();
  const [deleteClinic] = useDeleteClinic();
  const { handleError } = useGraphQLError();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingClinic, setEditingClinic] = useState<Clinic | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Get regions and districts from translations
  const regions = [
    { key: 'hk-island', value: t('regions.hongKongIsland') },
    { key: 'kowloon', value: t('regions.kowloon') },
    { key: 'new-territories', value: t('regions.newTerritories') },
    { key: 'outlying-islands', value: t('regions.outlyingIslands') }
  ];

  // All districts with their translations
  const allDistricts = [
    { key: 'central-western', value: t('districts.central-western') },
    { key: 'eastern', value: t('districts.eastern') },
    { key: 'southern', value: t('districts.southern') },
    { key: 'wan-chai', value: t('districts.wan-chai') },
    { key: 'sham-shui-po', value: t('districts.sham-shui-po') },
    { key: 'kowloon-city', value: t('districts.kowloon-city') },
    { key: 'kwun-tong', value: t('districts.kwun-tong') },
    { key: 'wong-tai-sin', value: t('districts.wong-tai-sin') },
    { key: 'yau-tsim-mong', value: t('districts.yau-tsim-mong') },
    { key: 'islands', value: t('districts.islands') },
    { key: 'kwai-tsing', value: t('districts.kwai-tsing') },
    { key: 'north', value: t('districts.north') },
    { key: 'sai-kung', value: t('districts.sai-kung') },
    { key: 'sha-tin', value: t('districts.sha-tin') },
    { key: 'tai-po', value: t('districts.tai-po') },
    { key: 'tsuen-wan', value: t('districts.tsuen-wan') },
    { key: 'tuen-mun', value: t('districts.tuen-mun') },
    { key: 'yuen-long', value: t('districts.yuen-long') },
    { key: 'cheung-chau', value: t('districts.cheung-chau') },
    { key: 'lamma-island', value: t('districts.lamma-island') },
    { key: 'lantau-island', value: t('districts.lantau-island') },
    { key: 'peng-chau', value: t('districts.peng-chau') }
  ];

  const clinics = data?.clinics || [];

  // Throttle search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredClinics = clinics.filter((clinic: Clinic) => {
    const matchesSearch = 
      (clinic.internal_name || clinic.name_zh_hk || clinic.name_en || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      clinic.name_en.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      clinic.name_zh_hk.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      clinic.address.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      clinic.phone_number.includes(debouncedSearchTerm);
    
    const matchesStatus = statusFilter === 'all' || clinic.status === statusFilter;
    const matchesRegion = regionFilter === 'all' || clinic.region === regionFilter;
    
    return matchesSearch && matchesStatus && matchesRegion;
  });

  const handleCreateClinic = async (values: any) => {
    try {
      setIsSubmitting(true);
      await createClinic({
        variables: {
          createClinicDto: {
            ...values,
            tenant: 'default'
          }
        }
      });
      setShowCreateModal(false);
      form.resetFields();
      message.success(t('clinics.clinicCreated'));
    } catch (error) {
      console.error('Error creating clinic:', error);
      message.error({ content: handleError(error), duration: 6 });
      // Keep dialog open on error so user can fix and retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateClinic = async (id: string, values: any) => {
    try {
      setIsSubmitting(true);
      await updateClinic({
        variables: {
          id,
          updateClinicDto: values
        }
      });
      setEditingClinic(null);
      form.resetFields();
      message.success(t('clinics.clinicUpdated'));
    } catch (error) {
      console.error('Error updating clinic:', error);
      message.error({ content: handleError(error), duration: 6 });
      // Keep dialog open on error so user can fix and retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClinic = async (id: string) => {
    try {
      await deleteClinic({
        variables: { id }
      });
      message.success(t('clinics.clinicDeleted'));
    } catch (error) {
      console.error('Error deleting clinic:', error);
      message.error(handleError(error));
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'success', text: t('clinics.statuses.active') },
      inactive: { color: 'warning', text: t('clinics.statuses.inactive') },
      maintenance: { color: 'error', text: t('clinics.statuses.maintenance') }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
    return <Tag color={config.color as any}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: t('clinics.clinicName'),
      key: 'name',
      render: (clinic: Clinic) => (
        <div>
          <div style={{ fontWeight: 500 }}>{clinic.internal_name || clinic.name_zh_hk || clinic.name_en}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{clinic.name_zh_hk}</Text>
        </div>
      ),
    },
    {
      title: t('clinics.address'),
      key: 'address',
      render: (clinic: Clinic) => (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <EnvironmentOutlined style={{ marginRight: 8, marginTop: 2, color: '#8c8c8c' }} />
          <div>
            <div style={{ fontSize: '14px' }}>{clinic.address}</div>
            {clinic.building_name && (
              <Text type="secondary" style={{ fontSize: '12px' }}>{clinic.building_name}</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('clinics.contact'),
      key: 'contact',
      render: (clinic: Clinic) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
            <Text style={{ fontSize: '12px' }}>{clinic.phone_number}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <MailOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
            <Text style={{ fontSize: '12px' }}>{clinic.email}</Text>
          </div>
        </div>
      ),
    },
    {
      title: t('clinics.region'),
      key: 'region',
      render: (clinic: Clinic) => {
        const regionObj = regions.find(r => r.key === clinic.region);
        const districtObj = allDistricts.find(d => d.key === clinic.district);
        return (
          <div>
            <div style={{ fontSize: '14px' }}>{regionObj ? regionObj.value : clinic.region}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {districtObj ? districtObj.value : t(`districts.${clinic.district}`)}
              {clinic.district_name && ` (${clinic.district_name})`}
            </Text>
          </div>
        );
      },
    },
    {
      title: t('common.status'),
      key: 'status',
      render: (clinic: Clinic) => getStatusTag(clinic.status),
    },
    {
      title: t('clinics.services'),
      key: 'services',
      render: (clinic: Clinic) => (
        <div style={{ maxWidth: 200 }}>
          {clinic.services && clinic.services.length > 0 ? (
            <div style={{ fontSize: '12px' }}>
              {clinic.services.slice(0, 2).join(', ')}
              {clinic.services.length > 2 && ` +${clinic.services.length - 2} ${t('clinics.more')}`}
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>{t('clinics.noServicesListed')}</Text>
          )}
        </div>
      ),
    },
    {
      title: t('clinics.externalInfo'),
      key: 'external',
      render: (clinic: Clinic) => (
        <div style={{ fontSize: '12px' }}>
          {clinic.external_id && (
            <div>
              <Text type="secondary">ID: {clinic.external_id.substring(0, 8)}...</Text>
            </div>
          )}
          {clinic.iframe && (
            <div>
              <Text type="secondary">📍 Map</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (clinic: Clinic) => (
        <Space>
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingClinic(clinic);
                form.setFieldsValue(clinic);
              }}
            />
          </Tooltip>
          <Popconfirm
            title={t('clinics.deleteConfirm')}
            onConfirm={() => handleDeleteClinic(clinic.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('clinics.loadingClinics')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>{t('clinics.title')}</Title>
            <Text type="secondary">{t('clinics.subtitle')}</Text>
          </Col>
        </Row>
        
        <Card>
          <Alert
            message={t('clinics.errorLoadingClinics')}
            description={handleError(error)}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>{t('clinics.title')}</Title>
          <Text type="secondary">{t('clinics.subtitle')}</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setShowCreateModal(true);
              form.resetFields();
            }}
          >
            {t('clinics.addClinic')}
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder={t('clinics.searchClinics')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('common.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('clinics.allStatus')}</Option>
              <Option value="active">{t('clinics.statuses.active')}</Option>
              <Option value="inactive">{t('clinics.statuses.inactive')}</Option>
              <Option value="maintenance">{t('clinics.statuses.maintenance')}</Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('clinics.region')}
              value={regionFilter}
              onChange={setRegionFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('clinics.allRegions')}</Option>
              {regions.map(region => (
                <Option key={region.key} value={region.key}>{region.value}</Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Button icon={<FilterOutlined />} style={{ width: '100%' }}>
              {t('common.export')}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Clinics Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredClinics}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('clinics.paginationInfo', { start: range[0], end: range[1], total }),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <ClinicModal
        visible={showCreateModal || !!editingClinic}
        clinic={editingClinic}
        regions={regions}
        districts={allDistricts}
        isSubmitting={isSubmitting}
        form={form}
        onCancel={() => {
          setShowCreateModal(false);
          setEditingClinic(null);
          form.resetFields();
        }}
        onSubmit={(values) => {
          if (editingClinic) {
            handleUpdateClinic(editingClinic.id, values);
          } else {
            handleCreateClinic(values);
          }
        }}
      />
    </div>
  );
};

interface ClinicModalProps {
  visible: boolean;
  clinic?: Clinic | null;
  regions: Array<{ key: string; value: string }>;
  districts: Array<{ key: string; value: string }>;
  isSubmitting: boolean;
  form: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

const ClinicModal: React.FC<ClinicModalProps> = ({ 
  visible, 
  clinic, 
  regions, 
  districts, 
  isSubmitting, 
  form, 
  onCancel, 
  onSubmit 
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={clinic ? t('clinics.editClinic') : t('clinics.addNewClinic')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          status: 'active',
          emergency_services: false,
          walk_in_enabled: false,
          online_consultation_enabled: false,
          internal_name: '',
          appointment_booking_enabled: false,
          name_zh_cn: '',
          postal_code: '',
          registration_number: ''
        }}
      >
        <Form.Item
          label={t('clinics.internalName')}
          name="internal_name"
          rules={[{ required: true, message: t('clinics.pleaseInputInternalName') }]}
        >
          <Input placeholder={t('clinics.enterInternalName')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={t('clinics.englishName')}
              name="name_en"
              rules={[{ required: true, message: t('clinics.pleaseInputEnglishName') }]}
            >
              <Input placeholder={t('clinics.enterEnglishName')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('clinics.chineseName')}
              name="name_zh_hk"
              rules={[{ required: true, message: t('clinics.pleaseInputChineseName') }]}
            >
              <Input placeholder={t('clinics.enterChineseName')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('clinics.chineseSimplifiedName')}
              name="name_zh_cn"
            >
              <Input placeholder={t('clinics.enterChineseSimplifiedName')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('clinics.address')}
          name="address"
          rules={[{ required: true, message: t('clinics.pleaseInputAddress') }]}
        >
          <Input.TextArea rows={3} placeholder={t('clinics.enterFullAddress')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={t('clinics.phoneNumber')}
              name="phone_number"
              rules={[{ required: true, message: t('clinics.pleaseInputPhoneNumber') }]}
            >
              <Input placeholder={t('clinics.enterPhoneNumber')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('clinics.email')}
              name="email"
              rules={[
                { required: true, message: t('clinics.pleaseInputEmail') },
                { type: 'email', message: t('clinics.pleaseEnterValidEmail') }
              ]}
            >
              <Input placeholder={t('clinics.enterEmailAddress')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('clinics.postalCode')}
              name="postal_code"
            >
              <Input placeholder={t('clinics.enterPostalCode')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('clinics.registrationNumber')}
              name="registration_number"
            >
              <Input placeholder={t('clinics.enterRegistrationNumber')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={t('common.status')}
              name="status"
            >
              <Select>
                <Option value="active">{t('clinics.statuses.active')}</Option>
                <Option value="inactive">{t('clinics.statuses.inactive')}</Option>
                <Option value="maintenance">{t('clinics.statuses.maintenance')}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('clinics.region')}
              name="region"
              rules={[{ required: true, message: t('clinics.pleaseSelectRegion') }]}
            >
              <Select placeholder={t('clinics.selectRegion')}>
                {regions.map(region => (
                  <Option key={region.key} value={region.key}>{region.value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('clinics.district')}
              name="district"
              rules={[{ required: true, message: t('clinics.pleaseSelectDistrict') }]}
            >
              <Select placeholder={t('clinics.selectDistrict')}>
                {districts.map(district => (
                  <Option key={district.key} value={district.key}>{district.value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('clinics.buildingName')} name="building_name">
              <Input placeholder={t('clinics.enterBuildingName')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('clinics.websiteUrl')} name="website_url">
              <Input placeholder={t('clinics.enterWebsiteUrl')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('clinics.externalId')} name="external_id">
              <Input placeholder={t('clinics.enterExternalId')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('clinics.districtId')} name="district_id">
              <Input type="number" placeholder={t('clinics.enterDistrictId')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('clinics.districtName')} name="district_name">
              <Input placeholder={t('clinics.enterDistrictName')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('clinics.iframe')} name="iframe">
              <Input.TextArea rows={2} placeholder={t('clinics.enterIframe')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label={t('clinics.floor')} name="floor">
              <Input placeholder={t('clinics.floor')} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={t('clinics.unit')} name="unit">
              <Input placeholder={t('clinics.unit')} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={t('clinics.postalCode')} name="postal_code">
              <Input placeholder={t('clinics.postalCode')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('clinics.description')} name="description">
          <Input.TextArea rows={3} placeholder={t('clinics.enterDescription')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="emergency_services" valuePropName="checked">
              <Checkbox>{t('clinics.emergencyServices')}</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="walk_in_enabled" valuePropName="checked">
              <Checkbox>{t('clinics.walkInEnabled')}</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="online_consultation_enabled" valuePropName="checked">
              <Checkbox>{t('clinics.onlineConsultation')}</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="appointment_booking_enabled" valuePropName="checked">
              <Checkbox>{t('clinics.appointmentBooking')}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isSubmitting}
            >
              {clinic ? t('clinics.updateClinic') : t('clinics.createClinic')}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default Clinics; 